import { User } from './../../../app/@shared/models/user.model';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as AccountActions from './account.actions';
import { Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { combineLatest, forkJoin, of } from 'rxjs';
import { globalFailure } from './../../../app/@store/common.actions';
import {
  getAccountListConfig,
  getAccountsMeta,
} from './../../../app/@store/account/account.selectors';
import { Account } from './../../../app/@shared/models/account.model';
import { IMeta } from './../../../app/@store/request-options.interface';
import { PublicPartnerApiService } from './../../../app/@shared/services/publicpartnerapi.service';
import { ContractManagementApiService } from './../../../app/@shared/services/contractmanagementapi.service';
import { loginSuccess } from '../auth/auth.actions';
import { LegacyPartnerApiService } from './../../../app/@shared/services/legacypartnerapi.service';
import { activateSuccess } from '../new-account/new-account.actions';
import { loadUser, loadUserSuccess } from '../user/user.actions';
import { HttpParams } from '@angular/common/http';
import { AccountResponse } from './../../../app/@shared/models/account_response.model';
import { contractmanagement_api } from '@gridscale/gs-services/contractmanagement_api'; import { AccountsResponse } from './../../../app/@shared/models/accounts_response.model';
import { AccountUsersResponse } from './../../../app/@shared/models/account_users_response.model';
import { AccountFeatureFlagsResponse } from './../../../app/@shared/models/account_feature_flags_response.model';
import * as _ from 'lodash';
import { BillingAddressResponse } from './../../../app/@shared/models/billing_address.model';
import { selectRouteParam } from '../router/router.selector';
import { PaymentMethodsWhitelistResponse } from '../../@shared/models/paymentMethod.model';
import { isPartnerLoaded } from '../partner/partner.selectors';

const ALL_PROJECTS_PAGE_SIZE = 300;

@Injectable()
export class AccountEffects {
  loadContracts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadAccounts),
      filter((action) => action.fromBatch !== true),
      withLatestFrom(this.store.select(getAccountListConfig)),
      concatMap(([action, config]) =>
        this.api
          .get<AccountsResponse>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts',
            _.omitBy(config, c => c === undefined) as HttpParams
          )
          .pipe(
            map((result) => {
              const total = result.contracts[0]?.total_row_count || 0;

              const meta: IMeta = {
                count: result.contracts.length,
                limit: _.get(config, 'limit', 10),
                page: 0,
                total,
              };

              return AccountActions.loadAccountsSuccess({
                data: result.contracts || [],
                meta,
              });
            }),
            catchError((error) =>
              of(
                AccountActions.loadAccountsFailure({ error }),
                globalFailure({ error })
              )
            )
          )
      )
    )
  );
  loadContractsTotal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AccountActions.loadAccountsTotal,
        loginSuccess,
        AccountActions.patchAccountSuccess,
        AccountActions.deleteAccountSuccess,
        AccountActions.createAccountSuccess
      ),
      concatMap((action) => {
        return this.api
          .get<AccountsResponse>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts',
            {
              fields: ['object_uuid'],
              filter: ['deleted=false'],
              limit: '1',
              page: '0',
            }
          )
          .pipe(
            map((result) => {
              const total = result.contracts[0]?.total_row_count || 0;

              return AccountActions.loadAccountsTotalSuccess({
                total,
              });
            }),
            catchError((error) =>
              of(
                AccountActions.loadContractsTotalFailure({ error }),
                globalFailure({ error })
              )
            )
          );
      })
    )
  );
  loadContractsNext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadAccountsNext),
      withLatestFrom(
        combineLatest([
          this.store.select(getAccountsMeta),
          this.store.select(getAccountListConfig),
        ])
      ),
      concatMap(([action, [_meta, _listConfig]]) => {
        const nextPage = _.get(_meta, 'page', 0) + 1;
        const totalPages = Math.ceil(
          _.get(_meta, 'total', 0) / _.get(_meta, 'limit', 10)
        );

        const config = { ..._listConfig };

        if (nextPage <= totalPages) {
          config.page = nextPage;
        }

        return this.api
          .get<AccountsResponse>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts',
            new HttpParams({ fromObject: { ..._.omitBy(config, c => c === undefined) } })
          )
          .pipe(
            map((result) => {
              const total = result.contracts[0]?.total_row_count || 0;

              const meta: IMeta = {
                count: result.contracts.length,
                limit: config.limit || 10,
                page: config.page || 0,
                total,
              };

              return AccountActions.loadAccountsNextSuccess({
                data: result.contracts || [],
                meta,
              });
            }),
            catchError((error) =>
              of(
                AccountActions.loadAccountsNextFailure({ error }),
                globalFailure({ error })
              )
            )
          );
      })
    )
  );
  loadFavorites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AccountActions.loadFavAccounts,
        AccountActions.toggleFavoriteSuccess
      ),
      concatMap((action) =>
        this.api
          .get<AccountsResponse>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts',
            {
              field: ['name'],
              filter: ['favorite=true'],
            }
          )
          .pipe(
            map((result) => {
              return AccountActions.loadFavAccountsSuccess({
                data: result.contracts || [],
              });
            }),
            catchError((error) =>
              of(
                AccountActions.loadFavAccountsFailure({ error }),
                globalFailure({ error })
              )
            )
          )
      )
    )
  );
  patchContractUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.patchAccountUser),
      filter((action) => action.fromBatch !== true),
      concatMap((action) =>
        this.api
          .patch<void>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              action.account_uuid +
              '/users/' +
              action.user_uuid,
            { ...action.data } as HttpParams
          )
          .pipe(
            map(() => {
              return AccountActions.patchAccountUserSuccess({
                account_uuid: action.account_uuid,
                user_uuid: action.user_uuid,
              });
            }),
            catchError((error) =>
              of(
                AccountActions.patchContractUserFailure({ error }),
                globalFailure({ error })
              )
            )
          )
      )
    )
  );
  /**
   * Loads only the first Object to get the Meta Data
   */
  loadAllContractsProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadAllAccountProjects),
      concatMap((action) =>
        this.store.select(isPartnerLoaded).pipe(
          filter((isPartnerLoaded) => !!isPartnerLoaded),
          map((action) => action)
        )
      ),
      switchMap((action) =>
        this.api
          .get<AccountsResponse>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts?page=0&limit=1&fields=object_uuid',
            {}
          )
          .pipe(
            map((result) => {
              const total = result.contracts[0]?.total_row_count || 0;
              return Math.ceil(total / ALL_PROJECTS_PAGE_SIZE);
            }),
            switchMap((pageCount) => {
              const actions = [
                AccountActions.loadAllAccountsProjectsSuccess({
                  count: pageCount,
                  page: 0,
                  page_size: 0,
                }),
              ];
              for (let index = 0; index < pageCount; index++) {
                actions.push(
                  AccountActions.loadAllContractsProjectsSingleCallNames({
                    page: index,
                    page_size: ALL_PROJECTS_PAGE_SIZE,
                    count: pageCount,
                  })
                );
              }
              return actions;
            }),
            catchError((error) =>
              of(
                AccountActions.loadAllContractsProjectsFailure({ error }),
                globalFailure({ error })
              )
            )
          )
      )
    )
  );

  /**
   * Loads a single Pages of contract names only
   */
  loadAllContractsProjectsSingleCallNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadAllContractsProjectsSingleCallNames),
      concatMap((action) =>
        this.api
          .get<Partial<AccountsResponse>>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts?fields=name,object_uuid,relations&page=' +
              action.page +
              '&page_size=' +
              action.page_size,
            {}
          )
          .pipe(
            map((result) => {
              return AccountActions.loadAllAccountsProjectsSingleCallSuccess({
                result: result.contracts || [],
              });
            }),
            catchError((error) =>
              of(
                AccountActions.loadAllContractsProjectsSingleCallFailure({
                  error,
                }),
                globalFailure({ error })
              )
            )
          )
      )
    )
  );
  postContractUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.postContractUser),
      filter((action) => action.fromBatch !== true),
      concatMap((action) =>
        this.api
          .post<void>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              action.account_uuid +
              '/users',
            { user_uuid: action.user_uuid, user_role: action.user_role }
          )
          .pipe(
            map((result) =>
              AccountActions.postAccountUserSuccess({
                account_uuid: action.account_uuid,
                user_uuid: action.user_uuid,
              })
            ),
            catchError((error) =>
              of(
                AccountActions.postContractUserFailure({ error }),
                globalFailure({ error })
              )
            )
          )
      )
    )
  );
  removeContractUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.removeAccountUser),
      filter((action) => action.fromBatch !== true),
      concatMap((action) =>
        this.api
          .delete<void>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              action.account_uuid +
              '/users/' +
              action.user_uuid
          )
          .pipe(
            map((result) =>
              AccountActions.removeAccountUserSuccess({
                account_uuid: action.account_uuid,
                user_uuid: action.user_uuid,
              })
            ),
            catchError((error) =>
              of(
                AccountActions.removeAccountUserFailure({ error }),
                globalFailure({ error })
              )
            )
          )
      )
    )
  );
  toggleFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.toggleFavorite),
      filter((action) => action.fromBatch !== true),
      concatMap((action) => {
        return this.api
          .patch<void>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              action.account_uuid,
            { favorite: action.favorite }
          )
          .pipe(
            map((result) =>
              AccountActions.toggleFavoriteSuccess({
                account_uuid: action.account_uuid,
                favorite: action.favorite,
              })
            ),
            catchError((error) =>
              of(
                AccountActions.toggleFavoriteFailure({
                  account_uuid: action.account_uuid,
                  error: error,
                }),
                globalFailure({ error })
              )
            )
          );
      })
    )
  );
  loadAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AccountActions.loadAccount,
        AccountActions.patchContractLimitsSuccess,
        AccountActions.patchAccountPaymentModeSuccess,
        AccountActions.patchAccountFeatureFlagSuccess,
        AccountActions.patchAccountSuccess,
        AccountActions.postAccountUserSuccess,
        AccountActions.removeAccountUserSuccess,
        AccountActions.patchAccountUserSuccess,
        AccountActions.patchAccountLegacySuccess,
        AccountActions.patchBillingAddressSuccess,
        AccountActions.toggleFavoriteSuccess,
        AccountActions.patchPaymentMethodsWhitelistSuccess,
        AccountActions.patchContractDisconnectModeSuccess,
        AccountActions.patchContractDisconnectedSuccess,
        activateSuccess
      ),
      filter((action) => action.fromBatch !== true),
      concatMap((action) =>
        forkJoin([
          this.api.get<Partial<AccountResponse>>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              action.account_uuid
          ),
          this.contractManagementApi.get<
            Partial<contractmanagement_api.ContractDetailResponse>
          >('/management/v2/contracts/' + action.account_uuid),
          this.contractManagementApi.get<
            Partial<contractmanagement_api.ContractBillingAddressResponse>
          >(
            '/management/v2/contracts/' +
              action.account_uuid +
              '/billingAddress'
          ),
          this.contractManagementApi.get<
            Partial<contractmanagement_api.ContractDetailsResponse>
          >(
            '/management/v2/contracts/' +
              action.account_uuid +
              '/contractDetails'
          )/*,
          this.contractManagementApi.get<
            Partial<PaymentMethodsWhitelistResponse>
          >(
            '/management/v2/contracts/' +
              action.account_uuid +
              '/payment-methods-whitelist'
          ),*/
        ]).pipe(
          map((results) => {
            return AccountActions.loadAccountSuccess({
              account_uuid: action.account_uuid,
              data: results[0].contract || {},
              management_data: results[1]?.data?.attributes || {},
              billing_address: results[2]?.data?.attributes || null,
              details: results[3]?.data?.attributes || {},
              payment_methods_whitelist: /*results[4]?.data || */[],
            });
          }),
          catchError((error) =>
            of(
              AccountActions.loadAccountFailure({
                error,
                account_uuid: action.account_uuid,
              }),
              globalFailure({ error })
            )
          )
        )
      )
    )
  );
  loadAccountOnly$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadAccountOnly),
      filter((action) => action.fromBatch !== true),
      concatMap((_action) => {
        return this.api
          .get<Partial<AccountResponse>>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              _action.account_uuid
          )
          .pipe(
            map((result) =>
              AccountActions.loadAccountOnlySuccess({
                account_uuid: _action.account_uuid,
                data: result.contract || {},
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.loadAccountOnlyFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          );
      })
    )
  );
  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AccountActions.postAccountUserSuccess,
        AccountActions.removeAccountUserSuccess,
        AccountActions.patchAccountUserSuccess
      ),
      filter((action) => action.fromBatch !== true),
      map((action) => loadUser({ user_uuid: action.user_uuid }))
    )
  );
  patchContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.patchAccount),
      filter((action) => action.fromBatch !== true),
      concatMap((_action) => {
        return this.api
          .patch<User>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              _action.account_uuid,
            _action.data as HttpParams
          )
          .pipe(
            map((result) =>
              AccountActions.patchAccountSuccess({
                request_id: '-1',
                account_uuid: _action.account_uuid,
                data: _action.data,
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.patchAccountFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          );
      })
    )
  );
  patchContractLegacy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.patchAccountLegacy),
      filter((action) => action.fromBatch !== true),
      concatMap((_action) => {
        return this.legacyapi
          .patch<{ object_uuid: string }>(
            '/api/v1/tenants/' + _action.account_uuid,
            _action.data
          )
          .pipe(
            map((result) =>
              AccountActions.patchAccountLegacySuccess({
                request_id: '-1',
                account_uuid: _action.account_uuid,
                data: _action.data,
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.patchContractLegacyFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          );
      })
    )
  );
  deleteContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.deleteAccount),
      filter((action) => action.fromBatch !== true),
      concatMap((_action) => {
        return this.api
          .delete<Account>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              _action.account_uuid
          )
          .pipe(
            map(() =>
              AccountActions.deleteAccountSuccess({
                request_id: '-1',
                account_uuid: _action.account_uuid,
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.deleteAccountFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          );
      })
    )
  );
  loadContractUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadAccountUsers, AccountActions.patchAccountUser),
      concatMap((_action) =>
        this.api
          .get<Partial<AccountUsersResponse>>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              _action.account_uuid +
              '/users'
          )
          .pipe(
            map((result) =>
              AccountActions.loadAccountUsersSuccess({
                account_uuid: _action.account_uuid,
                data: result.user_relations || [],
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.loadAccountUsersFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          )
      )
    )
  );
  // if a user is patched and it is related to current selected contract, reload the contracts-users-list
  loadContractUsersOnUserLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserSuccess),
      withLatestFrom(this.store.select(selectRouteParam('account_uuid'))),
      map(([action, selectedAccountUuid]) => {
        if (action.data?.relations?.contracts && selectedAccountUuid) {
          if (
            _.find(action.data.relations.contracts, {
              contract_uuid: selectedAccountUuid,
            })
          ) {
            return selectedAccountUuid;
          }
        }
        return undefined;
      }),
      filter((loadAccountUuid) => !!loadAccountUuid),
      concatMap((loadAccountUuid) =>
        this.api
          .get<Partial<AccountUsersResponse>>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              loadAccountUuid +
              '/users'
          )
          .pipe(
            map((result) =>
              AccountActions.loadAccountUsersSuccess({
                account_uuid: loadAccountUuid,
                data: result.user_relations || [],
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.loadAccountUsersFailure({
                  error,
                  account_uuid: loadAccountUuid,
                }),
                globalFailure({ error })
              );
            })
          )
      )
    )
  );
  loadContractFeatureFlag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AccountActions.loadAccountFeatureFlag,
        AccountActions.patchAccountFeatureFlagSuccess
      ),
      concatMap((_action) =>
        this.api
          .get<Partial<AccountFeatureFlagsResponse>>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              _action.account_uuid +
              '/feature_flags'
          )
          .pipe(
            map((result) =>
              AccountActions.loadAccountFeatureFlagSuccess({
                account_uuid: _action.account_uuid,
                data: result.feature_flags || [],
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.loadAccountFeatureFlagFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          )
      )
    )
  );
  patchContractFeatureFlag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.patchContractFeatureFlag),
      concatMap((_action) =>
        this.api
          .patch<void>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              _action.account_uuid +
              '/feature_flags',
            _action.data
          )
          .pipe(
            map((result) =>
              AccountActions.patchAccountFeatureFlagSuccess({
                account_uuid: _action.account_uuid,
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.patchContractFeatureFlagFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          )
      )
    )
  );
  patchContractLimits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.patchContractLimits),
      concatMap((_action) =>
        this.api
          .patch<void>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              _action.account_uuid +
              '/resource_limits',
            { values: _action.data }
          )
          .pipe(
            map((result) =>
              AccountActions.patchContractLimitsSuccess({
                account_uuid: _action.account_uuid,
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.patchContractLimitsFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          )
      )
    )
  );
  patchViaContractManagement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.patchViaContractManagement),
      concatMap((_action) =>
        this.contractManagementApi
          .patch(
            '/management/v2/contracts/' + _action.account_uuid,
            _action.data
          )
          .pipe(
            map((result) =>
              AccountActions.patchViaContractManagementSuccess({
                account_uuid: _action.account_uuid,
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.patchViaContractManagementFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          )
      )
    )
  );
  patchContractPaymentMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.patchContractPaymentMode),
      concatMap((_action) =>
        this.contractManagementApi
          .patch(
            '/management/v2/contracts/' + _action.account_uuid + '/paymentMode',
            _action.data
          )
          .pipe(
            map((result) =>
              AccountActions.patchAccountPaymentModeSuccess({
                account_uuid: _action.account_uuid,
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.patchContractPaymentModeFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          )
      )
    )
  );
  /**
   * this patches the MODE of contract disconnect
   */
  patchContractDisconnectMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.patchContractDisconnectMode),
      concatMap((_action) =>
        this.contractManagementApi
          .patch(
            '/management/v2/contracts/' +
              _action.account_uuid +
              '/disconnectMode',
            _action.data
          )
          .pipe(
            map((result) =>
              AccountActions.patchContractDisconnectModeSuccess({
                account_uuid: _action.account_uuid,
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.patchContractDisconnectModeFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          )
      )
    )
  );
  /**
   * this really disconnects/connects the account from the internet
   */
  patchContractDisconnectModeViaPartnerApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.patchContractDisconnected),
      concatMap((_action) =>
        this.api
          .patch(
            '/partners/##CURRENT_PARTNER_UUID##/contracts/' +
              _action.account_uuid,
            _action.data
          )
          .pipe(
            map((result) =>
              AccountActions.patchContractDisconnectedSuccess({
                account_uuid: _action.account_uuid,
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.patchContractDisconnectedFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          )
      )
    )
  );
  patchPaymentTerm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.patchPaymentTerm),
      concatMap((_action) =>
        this.contractManagementApi
          .patch(
            '/management/v2/contracts/' + _action.account_uuid + '/paymentTerm',
            _action.data
          )
          .pipe(
            map((result) =>
              AccountActions.patchPaymentTermSuccess({
                account_uuid: _action.account_uuid,
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.patchPaymentTermFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          )
      )
    )
  );
  createContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.createAccount),
      filter((action) => action.fromBatch !== true),
      concatMap((action) =>
        this.api
          .post<{ object_uuid: string }>(
            '/partners/##CURRENT_PARTNER_UUID##/contracts_batch',
            action.data
          )
          .pipe(
            map((result) => {
              return AccountActions.createAccountSuccess({
                account_uuid: result.object_uuid,
              });
            }),
            catchError((error) =>
              of(
                AccountActions.createAccountFailure({ error }),
                globalFailure({ error, whitelist: [401] })
              )
            )
          )
      )
    )
  );
  patchBillingAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.patchBillingAddress),
      concatMap((_action) =>
        this.contractManagementApi
          .patch<BillingAddressResponse>(
            '/management/v2/contracts/' +
              _action.account_uuid +
              '/billingAddress',
            _action.data as unknown as HttpParams
          )
          .pipe(
            map((result) =>
              AccountActions.patchBillingAddressSuccess({
                data: result.data?.attributes,
                account_uuid: _action.account_uuid,
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.patchBillingAddressFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          )
      )
    )
  );

  /*
  patchPaymentMethodsWhitelist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.patchPaymentMethodsWhitelist),
      concatMap((_action) =>
        this.contractManagementApi
          .patch<BillingAddressResponse>(
            '/management/v2/contracts/' +
              _action.account_uuid +
              '/payment-methods-whitelist',
            _action.data as unknown as HttpParams
          )
          .pipe(
            map((result) =>
              AccountActions.patchPaymentMethodsWhitelistSuccess({
                data: result.data?.attributes,
                account_uuid: _action.account_uuid,
              })
            ),
            catchError((error) => {
              return of(
                AccountActions.patchPaymentMethodsWhitelistFailure({
                  error,
                  account_uuid: _action.account_uuid,
                }),
                globalFailure({ error })
              );
            })
          )
      )
    )
  );*/

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly api: PublicPartnerApiService,
    private readonly contractManagementApi: ContractManagementApiService,
    private readonly legacyapi: LegacyPartnerApiService
  ) {}
}
